/**
 * Timezone-aware utilities for handling reservation dates and times
 * Ensures proper preservation of organization pickup/dropoff times
 */

import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { TZDate } from 'react-day-picker';

export interface TimezoneDateResult {
  /** The combined datetime in UTC (for database storage) */
  utcIsoString: string;
  /** The combined datetime in organization timezone (for display) */
  orgTimezoneString: string;
  /** The original date part */
  datePart: string;
  timePart: string;
  timezone: string;
}

/**
 * Combines a date string with a time string in the organization's timezone
 * @param dateString - ISO date string (e.g., "2024-12-10")
 * @param timeString - Time string in HH:MM format (e.g., "14:00")
 * @param timezone - Organization timezone (e.g., "America/Los_Angeles")
 * @returns Combined datetime information
 */
export function combineDateWithTime(
  dateString: string,
  timeString: string,
  timezone: string,
): TimezoneDateResult {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const [hours, minutes] = timeString.split(':').map(Number);

  // Create a TZDate in the organization's timezone
  // so the date/time combination represents the intended local time
  const tzDate = new TZDate(year, month, day, hours, minutes, 0, 0, timezone);

  // Convert to proper UTC ISO string (timezone-unaware)
  // TZDate.toISOString() returns timezone-aware string, but we need UTC
  const utcDate = new Date(tzDate.getTime());
  const utcIsoString = utcDate.toISOString();

  return {
    utcIsoString: utcIsoString,
    orgTimezoneString: tzDate.toString(),
    datePart: dateString,
    timePart: timeString,
    timezone,
  };
}

/**
 * Creates check-in datetime using organization's default dropoff time
 * Note: Check-in is when customer returns the RV back into the lot
 * @param checkInDate - ISO date string for check-in
 * @param defaultDropoffTime - Organization's default dropoff time (HH:MM)
 * @param timezone - Organization timezone
 * @returns Combined datetime information
 */
export function createCheckInDateTime(
  checkInDate: string,
  defaultDropoffTime: string,
  timezone: string,
): TimezoneDateResult {
  return combineDateWithTime(checkInDate, defaultDropoffTime, timezone);
}

/**
 * Creates check-out datetime using organization's default pickup time
 * Note: Check-out is when customer takes the RV out of the lot
 * @param checkOutDate - ISO date string for check-out
 * @param defaultPickupTime - Organization's default pickup time (HH:MM)
 * @param timezone - Organization timezone
 * @returns Combined datetime information
 */
export function createCheckOutDateTime(
  checkOutDate: string,
  defaultPickupTime: string,
  timezone: string,
): TimezoneDateResult {
  return combineDateWithTime(checkOutDate, defaultPickupTime, timezone);
}

/**
 * Converts a UTC ISO string back to organization timezone for display
 * @param utcIsoString - UTC ISO string from database
 * @param timezone - Organization timezone
 * @returns Display information
 */
export function convertUtcToOrgTimezone(
  utcIsoString: string,
  timezone: string,
): {
  orgTimezoneString: string;
  dateOnly: string;
  timeOnly: string;
  timezone: string;
} {
  const tzDate = new TZDate(new Date(utcIsoString), timezone);

  const year = tzDate.getFullYear();
  const month = String(tzDate.getMonth() + 1).padStart(2, '0');
  const day = String(tzDate.getDate()).padStart(2, '0');
  const hours = String(tzDate.getHours()).padStart(2, '0');
  const minutes = String(tzDate.getMinutes()).padStart(2, '0');

  return {
    orgTimezoneString: tzDate.toString(),
    dateOnly: `${year}-${month}-${day}`,
    timeOnly: `${hours}:${minutes}`,
    timezone,
  };
}

/**
 * Normalize a UTC ISO timestamp to its org-local calendar date (YYYY-MM-DD).
 *
 * Reservation timestamps store the org-local pickup/return time as UTC, so a
 * stay ending Aug 31 17:00 in a timezone behind UTC is stored with a Sep 1 UTC
 * calendar date; comparing that raw date against pricing-rule boundaries made
 * rules bleed onto adjacent stays (QA-1595). Date-only strings (rule start/end
 * dates) carry no timezone and are returned unchanged, which also makes this
 * safe to apply twice.
 */
export function toOrgDateOnly(
  dateStr: string,
  timezone: string | null | undefined,
): string;
export function toOrgDateOnly(
  dateStr: string | null | undefined,
  timezone: string | null | undefined,
): string | null | undefined;
export function toOrgDateOnly(
  dateStr: string | null | undefined,
  timezone: string | null | undefined,
): string | null | undefined {
  if (!dateStr || !timezone || !dateStr.includes('T')) {
    return dateStr;
  }
  return convertUtcToOrgTimezone(dateStr, timezone).dateOnly;
}

/**
 * Validates that a date string represents the expected date in the organization timezone
 * @param utcIsoString - UTC ISO string to validate
 * @param expectedDate - Expected date string (YYYY-MM-DD)
 * @param timezone - Organization timezone
 * @returns True if the UTC string represents the expected date in the org timezone
 */
function validateDateInTimezone(
  utcIsoString: string,
  expectedDate: string,
  timezone: string,
): boolean {
  const converted = convertUtcToOrgTimezone(utcIsoString, timezone);
  return converted.dateOnly === expectedDate;
}

/**
 * Debug helper to show how a date/time combination will be stored and displayed
 * @param dateString - Date string
 * @param timeString - Time string
 * @param timezone - Organization timezone
 * @returns Debug information
 */
export function debugDateTimeConversion(
  dateString: string,
  timeString: string,
  timezone: string,
) {
  const result = combineDateWithTime(dateString, timeString, timezone);
  const backConverted = convertUtcToOrgTimezone(result.utcIsoString, timezone);

  return {
    input: {
      date: dateString,
      time: timeString,
      timezone,
    },
    storage: {
      utcIsoString: result.utcIsoString,
      utcDate: new Date(result.utcIsoString).toUTCString(),
    },
    display: {
      orgTimezoneString: result.orgTimezoneString,
      backConverted: backConverted,
    },
    validation: {
      datePreserved: validateDateInTimezone(
        result.utcIsoString,
        dateString,
        timezone,
      ),
      timePreserved: backConverted.timeOnly === timeString,
    },
  };
}

/**
 * Legacy compatibility function for existing code that uses fromZonedTime pattern
 * Converts a date in organization timezone to UTC
 * @param dateString - Date string (YYYY-MM-DD)
 * @param timeString - Time string (HH:MM:SS or HH:MM)
 * @param timeZone - Organization timezone
 * @returns Date object in UTC
 */
export function parseInOrgTimeZoneToUTC(
  dateString: string,
  timeString: string,
  timeZone: string,
): Date {
  // Normalize time string to HH:MM format if it includes seconds
  const normalizedTime = timeString.includes(':')
    ? timeString.split(':').slice(0, 2).join(':')
    : timeString;

  const result = combineDateWithTime(dateString, normalizedTime, timeZone);
  return new Date(result.utcIsoString);
}

/**
 * Formats a UTC date string for display in the organization's timezone
 * Similar to the pattern used in PickupDropoffInfo.tsx
 */
export function formatUtcDateForDisplay(
  utcDateString: string,
  timezone: string,
  formatString: string,
): string {
  const date = parseISO(utcDateString);
  return formatInTimeZone(date, timezone, formatString);
}

/**
 * Returns the next calendar day for a yyyy-MM-dd date key, using pure UTC
 * arithmetic. Safe for iterating date keys without local-timezone leakage.
 */
export function nextDateKey(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/**
 * Converts a date range from the date picker to UTC midnight timestamps for Stripe API filtering
 * IMPORTANT: Stripe stores timestamps (like created, arrival_date) as Unix timestamps representing
 * midnight UTC for that calendar date. This function ensures we filter correctly by converting
 * the selected date to midnight UTC (start) and 23:59:59.999 UTC (end).
 *
 * @param range - The date range from the date picker (UTC ISO strings)
 * @param timezone - Organization timezone
 * @returns Date range with UTC midnight timestamps
 */
export function convertDateRangeForStripeFilter(
  range: { from: string; to?: string },
  timezone: string,
): { from: string; to?: string } {
  let from = range.from;
  let to = range.to;

  if (from) {
    const converted = convertUtcToOrgTimezone(from, timezone);
    from = new Date(converted.dateOnly + 'T00:00:00.000Z').toISOString();
  }

  if (to) {
    const converted = convertUtcToOrgTimezone(to, timezone);
    to = new Date(converted.dateOnly + 'T23:59:59.999Z').toISOString();
  }

  return { from, to };
}

/**
 * Converts an ISO string's time portion to start-of-day (00:00:00.000Z)
 * Useful for inclusive date range filtering
 */
export function convertToStartOfDay(dateString: string): string {
  if (!dateString) return dateString;
  return dateString.replace(
    /T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/,
    'T00:00:00.000Z',
  );
}

/**
 * Returns today's date string (YYYY-MM-DD) in the organization's timezone
 */
export function getTodayInTimezone(timezone: string): string {
  const now = new Date();
  const tzDate = new TZDate(now, timezone);
  const year = tzDate.getFullYear();
  const month = String(tzDate.getMonth() + 1).padStart(2, '0');
  const day = String(tzDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converts an ISO string's time portion to end-of-day (23:59:59.999Z)
 * Useful for inclusive date range filtering
 */
export function convertToEndOfDay(dateString: string): string {
  if (!dateString) return dateString;
  return dateString.replace(
    /T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/,
    'T23:59:59.999Z',
  );
}

/**
 * Returns the UTC ISO string for midnight (start of day) in the given timezone.
 * Use for timestamptz range filters where the boundary must respect the org's local day.
 */
export function convertToStartOfDayInTimezone(
  dateString: string,
  timezone: string,
): string {
  const date = new Date(dateString);
  const tzDate = new TZDate(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    0, 0, 0, 0,
    timezone,
  );
  return new Date(tzDate.getTime()).toISOString();
}

/**
 * Returns the UTC ISO string for end of day (23:59:59.999) in the given timezone.
 * Use for timestamptz range filters where the boundary must respect the org's local day.
 */
export function convertToEndOfDayInTimezone(
  dateString: string,
  timezone: string,
): string {
  const date = new Date(dateString);
  const tzDate = new TZDate(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    23, 59, 59, 999,
    timezone,
  );
  return new Date(tzDate.getTime()).toISOString();
}

/**
 * Formats a date-range bound for an export filename as MM.dd.yy.
 *
 * Date-range filters store each bound as an ISO string whose YYYY-MM-DD portion
 * is already the calendar date the user picked (the "from" bound is typically
 * midnight UTC of that date). Pull the date portion directly; running it
 * through `formatUtcDateForDisplay`/the org timezone would shift a midnight-UTC
 * "from" back a day in timezones behind UTC. Shared by every export filename.
 *
 * @param isoString - ISO date string (e.g. "2026-05-18T00:00:00.000Z")
 * @returns "MM.dd.yy" (e.g. "05.18.26"), or null if the string can't be parsed
 */
export function formatRangeDateForFilename(isoString: string): string | null {
  const datePart = isoString.split('T')[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${month}.${day}.${year.slice(-2)}`;
}
